import { readAuthUserRaw } from './api-config';
import type { User } from '../types/config';
import type { ModuleAccessLevel } from './moduleAccessPolicy';

const FEE_MODULE_CODES = new Set(['fees', 'fee']);

/** Endpoints portal / read-only fee users need to view and download their own invoices. */
const PORTAL_FEE_SELF_READ: Array<{ method: string; path: string }> = [
  { method: 'GET', path: '/fees/invoices' },
  { method: 'POST', path: '/fees/invoices/preview' },
];

function normalizePath(endpoint: string): string {
  return endpoint.split('?')[0].split('#')[0];
}

export function isPortalFeeSelfReadEndpoint(method: string, endpoint: string): boolean {
  const path = normalizePath(endpoint);
  const verb = method.toUpperCase();
  return PORTAL_FEE_SELF_READ.some((rule) => rule.method === verb && path.startsWith(rule.path));
}

function readStoredUser(): User | null {
  const raw = readAuthUserRaw();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function levelRank(level: ModuleAccessLevel | string | undefined): number {
  if (level === 'write') return 2;
  if (level === 'read') return 1;
  return 0;
}

function feesModuleEntry(user: User) {
  return user.moduleAccess?.find((entry) => {
    const code = entry.module_code.trim().toLowerCase();
    return FEE_MODULE_CODES.has(code) || code.endsWith('_fees') || code.endsWith('_fee');
  });
}

/** True when the session may read own fee invoices (portal user or fees module read). */
export function portalUserHasFeesReadAccess(): boolean {
  const user = readStoredUser();
  if (!user) return false;

  const perms = user.permissions ?? [];
  if (perms.includes('fees:manage') || perms.includes('fees:read')) return true;

  const entry = feesModuleEntry(user);
  if (!entry || entry.enabled === false) return false;
  if (levelRank(entry.access_level) >= 1) return true;

  const subs = entry.submodules ?? [];
  return subs.some((sub) => levelRank(sub.access_level) >= 1);
}

/**
 * Override explicit client-side api-access denials for self-service fee invoice reads.
 * Backend still enforces scope (own student only).
 */
export function shouldAllowPortalFeeSelfRead(method: string, endpoint: string): boolean {
  if (!isPortalFeeSelfReadEndpoint(method, endpoint)) return false;
  return portalUserHasFeesReadAccess();
}
